import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, Info, UserCog, Trophy, Award, FileText, HelpCircle, MessageSquare } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import './UserGuide.css';

const UserGuide: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin } = useUser();
  const [activeSection, setActiveSection] = useState('general');
  const sectionRefs = useRef<{ [key: string]: HTMLElement | null }>({});

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.3,
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    },
    exit: { opacity: 0, transition: { duration: 0.3 } }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.3 } }
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  // Define all guide sections
  const sections = [
    {
      id: 'general',
      title: 'General',
      icon: <Info className="w-4 h-4 mr-2" />,
      content: (
        <Card>
          <CardHeader>
            <CardTitle>Welcome to The League System</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <h3 className="text-lg font-medium">Getting Started</h3>
            <p>This platform is designed to facilitate debate rounds, track performance statistics, and manage debate activities. Here's how to get started:</p>
            
            <ul className="list-disc pl-6 space-y-2">
              <li>Your <strong>Dashboard</strong> shows your current and upcoming debate rounds</li>
              <li>Check your <strong>Performance Stats</strong> to track your progress over time</li>
              <li>View your <strong>Debate History</strong> to see past debates and results</li>
            </ul>
            
            <h3 className="text-lg font-medium mt-6">Setting Up Your Profile</h3>
            <p>Complete your profile to help in matching you with appropriate debates and teams:</p>
            <ol className="list-decimal pl-6 space-y-2">
              <li>Navigate to the Profile section from the user menu in the top right corner</li>
              <li>Fill in your biographical information</li>
              <li>Customize your avatar</li>
              <li>Save your changes</li>
            </ol>
            
            <h3 className="text-lg font-medium mt-6">User Interface</h3>
            <p>The platform supports both light and dark modes. Use the theme toggle in the top navigation bar to switch between them.</p>
            
            <div className="p-4 rounded-md mt-4" style={{ backgroundColor: 'var(--info-color-bg)' }}>
              <h4 className="text-md font-medium mb-2 flex items-center" style={{ color: 'var(--info-color)' }}>
                <HelpCircle className="w-4 h-4 mr-2" />
                Helpful Tip
              </h4>
              <p className="text-sm" style={{ color: 'var(--info-color)' }}>
                You can access this guide at any time by clicking on the "Help" option in the user menu.
              </p>
            </div>
            
            <h3 className="text-lg font-medium mt-6">Notifications</h3>
            <p>Important updates about rounds, results, and system messages will appear in your notification panel. Check it regularly for updates.</p>
          </CardContent>
        </Card>
      )
    },
    {
      id: 'debates',
      title: 'Debates',
      icon: <Trophy className="w-4 h-4 mr-2" />,
      content: (
        <Card>
          <CardHeader>
            <CardTitle>Participating in Debates</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <h3 className="text-lg font-medium">Joining a Round</h3>
            <p>To join a debate round:</p>
            <ol className="list-decimal pl-6 space-y-2">
              <li>Enter the round code provided by the moderator</li>
              <li>Wait in the waiting room until the round starts</li>
              <li>Once assigned a role, prepare for your position</li>
            </ol>
            
            <div className="rounded-lg p-4 mt-6" style={{ border: '1px solid var(--border-color)' }}>
              <h3 className="text-lg font-medium">Debate Formats</h3>
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div className="p-4 rounded-md" style={{ backgroundColor: 'var(--secondary-bg)' }}>
                  <h4 className="font-medium" style={{ color: 'var(--primary-color)' }}>British Parliamentary (BP)</h4>
                  <div className="mt-3">
                    <h5 className="text-sm font-medium mb-1">Structure:</h5>
                    <ul className="list-disc pl-5 text-sm space-y-1">
                      <li><strong>Opening Government:</strong> Prime Minister and Deputy Prime Minister</li>
                      <li><strong>Opening Opposition:</strong> Leader of Opposition and Deputy Leader of Opposition</li>
                      <li><strong>Closing Government:</strong> Government Member and Government Whip</li>
                      <li><strong>Closing Opposition:</strong> Opposition Member and Opposition Whip</li>
                    </ul>
                  </div>
                  <div className="mt-3">
                    <h5 className="text-sm font-medium mb-1">Timing:</h5>
                    <ul className="list-disc pl-5 text-sm space-y-1">
                      <li>Speeches: 7 minutes each</li>
                      <li>Protected time: First and last minute</li>
                      <li>Points of Information allowed between protected times</li>
                    </ul>
                  </div>
                </div>
                
                <div className="p-4 rounded-md" style={{ backgroundColor: 'var(--secondary-bg)' }}>
                  <h4 className="font-medium" style={{ color: 'var(--primary-color)' }}>Asian Parliamentary (AP)</h4>
                  <div className="mt-3">
                    <h5 className="text-sm font-medium mb-1">Structure:</h5>
                    <ul className="list-disc pl-5 text-sm space-y-1">
                      <li><strong>Government:</strong> Prime Minister, Deputy Prime Minister, Government Whip</li>
                      <li><strong>Opposition:</strong> Leader of Opposition, Deputy Leader of Opposition, Opposition Whip</li>
                    </ul>
                  </div>
                  <div className="mt-3">
                    <h5 className="text-sm font-medium mb-1">Timing:</h5>
                    <ul className="list-disc pl-5 text-sm space-y-1">
                      <li>Substantive speeches: 7 minutes each</li>
                      <li>Reply speeches: 4 minutes each (by PM and LO)</li>
                      <li>Protected time: First and last minute</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            
            <h3 className="text-lg font-medium mt-6">Before the Debate</h3>
            <ol className="list-decimal pl-6 space-y-2">
              <li>Review the motion and any provided information slides</li>
              <li>If you're assigned a speaking role, prepare your arguments</li>
              <li>Use your preparation time wisely</li>
            </ol>
            
            <h3 className="text-lg font-medium mt-6">Participating Step-by-Step</h3>
            <div className="step-guide">
              <div className="step-item">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h4 className="text-md font-medium">Join the Round</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Enter the round code on your dashboard and click "Join Round" to enter the waiting room.
                  </p>
                </div>
              </div>
              
              <div className="step-item">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h4 className="text-md font-medium">Check Your Role</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Once the chair assigns roles, you'll see your position and team on the screen. Confirm your role and prepare accordingly.
                  </p>
                </div>
              </div>
              
              <div className="step-item">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h4 className="text-md font-medium">Preparation Time</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Use your personal timer to organize your thoughts and create your arguments. Work with your team if applicable.
                  </p>
                </div>
              </div>
              
              <div className="step-item">
                <div className="step-number">4</div>
                <div className="step-content">
                  <h4 className="text-md font-medium">Deliver Your Speech</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    When it's your turn. Present your arguments clearly and respond to previous speakers.
                  </p>
                </div>
              </div>
              
              <div className="step-item">
                <div className="step-number">5</div>
                <div className="step-content">
                  <h4 className="text-md font-medium">Review Results</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    After all speeches, the adjudication panel will deliberate. Results will be announced and available on your dashboard.
                  </p>
                </div>
              </div>
            </div>
            
            <h3 className="text-lg font-medium mt-6">During the Debate</h3>
            <p>When it's your turn to speak, you'll see a timer on screen. Keep an eye on the timer to manage your speech time effectively.</p>
            <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-md mt-4">
              <h4 className="text-md font-medium text-amber-700 dark:text-amber-300 mb-2">Tips for Effective Debating</h4>
              <ul className="list-disc pl-5 text-sm text-amber-600 dark:text-amber-400 space-y-1">
                <li>Structure your arguments clearly with claims, reasoning, and evidence</li>
                <li>Listen carefully to other speakers and take notes</li>
                <li>Address key points made by the opposing team</li>
                <li>Be respectful to all participants</li>
              </ul>
            </div>
            
            <h3 className="text-lg font-medium mt-6">Rating Adjudicators</h3>
            <p>After each debate, you'll be able to rate the performance of adjudicators. This helps improve the quality of adjudication across the platform.</p>
          </CardContent>
        </Card>
      )
    },
    {
      id: 'adjudication',
      title: 'Adjudication',
      icon: <Award className="w-4 h-4 mr-2" />,
      content: (
        <Card>
          <CardHeader>
            <CardTitle>Adjudication Guide</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <h3 className="text-lg font-medium">Adjudicator Roles</h3>
            <div className="grid md:grid-cols-3 gap-4 mt-3">
              <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-md">
                <h4 className="font-medium text-purple-600 dark:text-purple-400">Chair Adjudicator</h4>
                <p className="text-sm mt-2">As Chair, you lead the adjudication panel, make the final decision in case of disagreements, and provide feedback to debaters.</p>
              </div>
              
              <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-md">
                <h4 className="font-medium text-purple-600 dark:text-purple-400">Panelist</h4>
                <p className="text-sm mt-2">Panelists contribute to the adjudication discussion, and rate the Chair Adjudicator.</p>
              </div>
              
              <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-md">
                <h4 className="font-medium text-purple-600 dark:text-purple-400">Trainee</h4>
                <p className="text-sm mt-2">Trainees observe and interact with the adjudication process for learning purposes but do not contribute to the final decision.</p>
              </div>
            </div>
            
            <h3 className="text-lg font-medium mt-6">Adjudication Process</h3>
            <p>Digital tools are provided to assist with adjudication:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Digital ballot for recording scores and rankings</li>
              <li>Record speaking time for each speaker (Chair)</li>
              <li>Feedback submission form (Chair)</li>
            </ul>
            
            <div className="screenshot-container mt-4">
              <img 
                src="/assets/guide-images/adjudication-interface.png" 
                alt="Screenshot of the Adjudication Interface" 
              />
              <div className="screenshot-caption">
                The Digital Chair Adjudicator Ballot interface for scoring and ranking teams
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6 mt-6">
              <div>
                <h3 className="text-lg font-medium">BP Adjudication Process</h3>
                <ol className="list-decimal pl-6 space-y-2 mt-3">
                  <li>Participate in the adjudication discussion</li>
                  <li>Rank all four teams from 1st to 4th position</li>
                  <li>Score each speaker keep a rate each adjudicator (if applicable)</li>
                  <li>The Chair will announce the final result</li>
                  <li>Submit your decisions through the platform</li>
                </ol>
              </div>
              
              <div>
                <h3 className="text-lg font-medium">AP Adjudication Process</h3>
                <ol className="list-decimal pl-6 space-y-2 mt-3">
                  <li>Participate in the adjudication discussion</li>
                  <li>Determine the winning side (Government or Opposition)</li>
                  <li>Score each speaker and rate each adjudicator (if applicable)</li>
                  <li>The Chair will announce the final result</li>
                  <li>Submit your decisions through the platform</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>
      )
    },
    {
      id: 'faq',
      title: 'FAQ',
      icon: <HelpCircle className="w-4 h-4 mr-2" />,
      content: (
        <Card>
          <CardHeader>
            <CardTitle>Frequently Asked Questions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-6">
              <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                <h3 className="text-md font-medium mb-2">What should I do if I lose connection during a debate?</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  If you lose connection, the system will try to reconnect you automatically. If you're unable to rejoin,
                  contact the tournament organizer. In some cases, the debate may need to be paused and resumed once you're reconnected.
                </p>
              </div>
              
              <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                <h3 className="text-md font-medium mb-2">How are teams assigned in rounds?</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  For practice rounds, teams are assigned randomly or based on preferences set by the organizer.
                  In competitive rounds, teams may be assigned based on ranking or preliminary results.
                </p>
              </div>
              
              <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                <h3 className="text-md font-medium mb-2">Can I use prepared materials during a debate?</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  This depends on the rules of the specific debate. In most formats, you can use materials prepared during
                  the preparation time but not pre-written speeches prepared before the motion was announced.
                </p>
              </div>
              
              <div className="pb-4">
                <h3 className="text-md font-medium mb-2">How do I improve my debate skills on the platform?</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Review your feedback from previous debates, participate in as many rounds as possible, 
                  and consider working with more experienced debaters for guidance and improvement strategies.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )
    },
    {
      id: 'support',
      title: 'Support',
      icon: <MessageSquare className="w-4 h-4 mr-2" />,
      content: (
        <Card>
          <CardHeader>
            <CardTitle>Contact Support</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>If you need additional help or have questions not covered in this guide, please reach out to our support team.</p>
            
            <div className="grid md:grid-cols-2 gap-6 mt-4">
              <div className="p-4 rounded-md" style={{ backgroundColor: 'var(--secondary-bg)' }}>
                <h3 className="text-lg font-medium mb-2">Email Support</h3>
                <p className="text-sm mb-3">For general inquiries and issues:</p>
                <a href="mailto:theleaguesystem@gmail.com" className="text-blue-600 dark:text-blue-400 font-medium">
                  theleaguesystem@gmail.com
                </a>
              </div>
              
              <div className="p-4 rounded-md" style={{ backgroundColor: 'var(--secondary-bg)' }}>
                <h3 className="text-lg font-medium mb-2">Feature Requests</h3>
                <p className="text-sm mb-3">Have ideas for improving the platform?</p>
                <Button variant="outline">
                  Submit Feedback
                </Button>
              </div>
            </div>
            
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-md mt-4">
              <h4 className="text-md font-medium text-green-700 dark:text-green-300 mb-2 flex items-center">
                <Info className="w-4 h-4 mr-2" />
                Response Time
              </h4>
              <p className="text-sm text-green-600 dark:text-green-400">
                We aim to respond to all support requests within 24-48 hours during business days.
              </p>
            </div>
          </CardContent>
        </Card>
      )
    }
  ];

  // Admin section is conditionally added
  if (isAdmin) {
    sections.push({
      id: 'admin',
      title: 'Admin Features',
      icon: <UserCog className="w-4 h-4 mr-2" />,
      content: (
        <Card>
          <CardHeader>
            <CardTitle>Admin Features Guide</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="text-lg font-medium">Round Management</h3>
              <p className="mt-2">As an admin, you have access to create, monitor, and manage debate rounds:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Create new rounds with specific formats and motions</li>
                <li>Assign participants to roles (debaters and adjudicators)</li>
                <li>Monitor ongoing rounds and view results</li>
                <li>Terminate or delete rounds when necessary</li>
              </ul>
              
              <div className="border rounded-md p-4 mt-4" style={{ backgroundColor: 'var(--secondary-bg)', borderColor: 'var(--border-color)', opacity: 0.95 }}>
                <h4 className="text-md font-medium mb-2 flex items-center">
                  <FileText className="w-4 h-4 mr-2" />
                  How to Create a New Round
                </h4>
                <ol className="list-decimal pl-5 text-sm space-y-2">
                  <li>Navigate to the Admin Dashboard</li>
                  <li>Click on "Create Round"</li>
                  <li>Fill in round details: format, motion, and and adjudicator cap</li>
                  <li>Start the allocation the to generate a unique round code</li>
                  <li>Share the code with participants and assign roles</li>
                </ol>
                
                <div className="screenshot-container mt-4">
                  <img 
                    src="/assets/guide-images/create-round-example.png" 
                    alt="Screenshot of the Create New Round interface" 
                  />
                  <div className="screenshot-caption">
                    Example of the Create New Round interface with fields for round details
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-medium">User Management</h3>
              <p className="mt-2">Manage user accounts and permissions through the Users Panel:</p>
              <div className="grid md:grid-cols-2 gap-4 mt-3">
                <div>
                  <h4 className="text-md font-medium mb-2">Account Management</h4>
                  <ul className="list-disc pl-5 text-sm space-y-2">
                    <li>Create new user accounts</li>
                    <li>Edit user information and roles</li>
                    <li>Activate or deactivate accounts</li>
                    <li>Reset user passwords</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-md font-medium mb-2">Permission Control</h4>
                  <ul className="list-disc pl-5 text-sm space-y-2">
                    <li>Assign administrator privileges</li>
                    <li>Set debater/adjudicator flair</li>
                    <li>Manage access to features</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-medium">System Logs</h3>
              <p className="mt-2">Access system logs to monitor platform activity and troubleshoot issues:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>View attendance logs to track participant engagement</li>
                <li>Check system logs for errors and warnings</li>
                <li>Export logs as CSV for further analysis</li>
                <li>Monitor login activity and security events</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-medium">Notification Management</h3>
              <p className="mt-2">Create and send notifications to specific users or groups:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Send announcements about upcoming events</li>
                <li>Notify users about system changes</li>
                <li>Customize notification content and recipients</li>
                <li>Schedule notifications for future delivery</li>
              </ul>
              
              <div className="border rounded-md p-4 mt-4" style={{ backgroundColor: 'var(--secondary-bg)', borderColor: 'var(--border-color)', opacity: 0.95 }}>
                <h4 className="text-md font-medium mb-2 flex items-center">
                  <FileText className="w-4 h-4 mr-2" />
                  Creating Bulk Notifications
                </h4>
                <ol className="list-decimal pl-5 text-sm space-y-2">
                  <li>Go to the Admin Dashboard and select "Manage Notifications"</li>
                  <li>Click "Create New Notification"</li>
                  <li>Compose your message with an optional link</li>
                  <li>Select "Send to All" or choose specific recipients</li>
                  <li>Preview and send the notification</li>
                </ol>
                
                <div className="screenshot-container mt-4">
                  <img 
                    src="/assets/guide-images/bulk-notifications-example.png" 
                    alt="Screenshot of the Bulk Notifications interface" 
                  />
                  <div className="screenshot-caption">
                    Example of the Bulk Notifications interface for creating and sending notifications to multiple users
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-medium">Stats & Analytics</h3>
              <p className="mt-2">Access platform analytics to gain insights:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>View participation statistics by user, team, or round</li>
                <li>Track platform usage metrics</li>
                <li>Generate reports on adjudicator performance</li>
                <li>Analyze motion trends and results</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      )
    });
  }

  // Effect to scroll to active section
  useEffect(() => {
    const ref = sectionRefs.current[activeSection];
    if (ref) {
      ref.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeSection]);

  return (
    <motion.div 
      className="p-4 w-full max-w-7xl mx-auto"
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={containerVariants}
    >
      <motion.div variants={itemVariants} className="flex items-center mb-6">
        <Button 
          variant="ghost" 
          size="icon" 
          className="mr-2"
          onClick={handleBack}
        >
          <ArrowLeft size={20} />
        </Button>
        <h1 className="text-3xl font-bold dark:text-[#F5F5F5] light:text-gray-900">
          User Guide
        </h1>
      </motion.div>

      <div className="flex flex-col md:flex-row gap-6">
        {/* Sidebar Navigation */}
        <motion.div 
          variants={itemVariants} 
          className="md:w-64 flex-shrink-0"
        >
          <div className="rounded-lg shadow-sm p-3 sticky top-4" style={{ backgroundColor: 'var(--card-bg)' }}>
            <div className="font-medium text-base mb-3">Contents</div>
            <nav>
              <ul className="space-y-1">
                {sections.map((section) => (
                  <li key={section.id}>
                    <button
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center px-3 py-2 text-sm rounded-md transition-colors ${
                        activeSection === section.id
                          ? 'bg-primary text-primary-foreground font-medium'
                          : 'hover:bg-muted'
                      }`}
                    >
                      {section.icon}
                      {section.title}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </motion.div>

        {/* Main Content */}
        <motion.div variants={itemVariants} className="flex-1">
          {sections.map((section) => (
            <div
              key={section.id}
              ref={(el) => { sectionRefs.current[section.id] = el; }}
              className={`guide-section ${
                activeSection === section.id ? 'block' : 'hidden'
              }`}
            >
              {section.content}
            </div>
          ))}
        </motion.div>
      </div>
    </motion.div>
  );
};

export default UserGuide;